'use client';

import { atom, useAtom } from 'jotai';
import type { Route } from 'next';
import Link from 'next/link';
import { useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { iconCoinSmall, iconEdit } from '../../../assets/images';
import utilsStyles from '../../../styles/modules/utils.module.scss';
import IconTextUnit from '../../ui/IconTextUnit/IconTextUnit';
import LinkListItem from '../../ui/LinkListItem/LinkListItem';
import styles from './CommonMenu.module.scss';

// メニュー開閉
// NOTE: 共通処理に移動したい
const isMenuOpenedAtom = atom(false);
const useIsMenuOpenedAtom = () => useAtom(isMenuOpenedAtom);

export interface MenuItem {
  id: string;
  iconSrc: string;
  text: string;
  onClick: () => void;
  showBadge?: boolean;
  isVisible?: boolean;
}

export interface ProfileSection {
  userName: string;
  editLink: string | Route;
  editIconSrc: string;
}

export interface CoinSection {
  label: string;
  coinAmount: number;
  coinIconSrc: string;
  formatCurrency: (amount: number) => string;
}

export interface CommonMenuProps {
  isOptionNotEntered?: boolean;
  hasPendingEntry?: boolean;
  hasAlertEcItem?: boolean;
  onLogout: () => void;
  userUnreadMessageBoxCount?: number;
  userCoin?: number;
  userName?: string;
  isFamikuji?: boolean;
  // 外部依存関数
  formatCurrency: (amount: number) => string;
  // メニューリスト
  menuItems: MenuItem[];
  // プロフィール・コイン表示
  profileSection?: ProfileSection;
  coinSection?: CoinSection | null;
  isExternal?: boolean;
}

function CommonMenu({ userCoin, userName, formatCurrency, menuItems, profileSection, coinSection }: CommonMenuProps) {
  const [isMenuOpenedValue, setIsMenuOpened] = useIsMenuOpenedAtom();
  const nodeRef = useRef(null);

  // デフォルト値の設定
  const defaultProfileSection: ProfileSection = {
    userName: userName || '',
    editLink: '/account/edit',
    editIconSrc: iconEdit.src,
  };

  const defaultCoinSection: CoinSection = {
    label: '保有しているコイン',
    coinAmount: userCoin ?? 0,
    coinIconSrc: iconCoinSmall.src,
    formatCurrency: formatCurrency,
  };

  const finalProfileSection = profileSection || defaultProfileSection;
  const finalCoinSection = coinSection || defaultCoinSection;

  return (
    <CSSTransition in={isMenuOpenedValue} timeout={200} classNames={styles} nodeRef={nodeRef} appear unmountOnExit>
      <div
        className={`${styles.componentStyle}`}
        onClick={() => {
          setIsMenuOpened(false);
        }}
        ref={nodeRef}
      >
        <div className={styles.container}>
          <section className={styles.profile}>
            <div className={styles.profileDetail}>
              <Link href={finalProfileSection.editLink as Route} className={styles.profileName}>
                <span className={utilsStyles.textEllipsis}>{finalProfileSection.userName}</span>
                &nbsp;
                <img src={finalProfileSection.editIconSrc} alt='Edit' className={utilsStyles.textIconSmall} />
              </Link>
            </div>
            {coinSection !== null && (
              <div className={utilsStyles.mtSmall}>
                <p className={styles.menuCoinLabel}>{finalCoinSection.label}</p>
                <div className={styles.menuCoin}>
                  <IconTextUnit
                    text={finalCoinSection.formatCurrency(finalCoinSection.coinAmount)}
                    imgSrc={finalCoinSection.coinIconSrc}
                    unit={'coin'}
                  />
                </div>
              </div>
            )}
          </section>
          <hr className={styles.hr} />
          <ul className={styles.menuList}>
            {menuItems
              .filter((item) => item.isVisible !== false)
              .map((item) => (
                <li key={item.id}>
                  <LinkListItem
                    iconSrc={item.iconSrc}
                    onClick={item.onClick}
                    text={item.text}
                    showBadge={item.showBadge}
                    id={item.id}
                  />
                </li>
              ))}
          </ul>
        </div>
      </div>
    </CSSTransition>
  );
}

export { useIsMenuOpenedAtom };
export default CommonMenu;
